import React, { useEffect, useRef, useState } from 'react';
import AdminOnly from '../components/AdminOnly';
import useProfile from '../hooks/useProfile';
import useAskAiChat from '../hooks/useAskAiChat';
import useAiStatus from '../hooks/reactQuery/useAiStatus';
import useAiConversations from '../hooks/reactQuery/useAiConversations';
import useAiConversation from '../hooks/reactQuery/useAiConversation';
import useDeleteAiConversation from '../hooks/reactQuery/useDeleteAiConversation';
import ConversationList from '../components/askAi/ConversationList';
import ChatTranscript from '../components/askAi/ChatTranscript';
import ChatComposer from '../components/askAi/ChatComposer';

const greeting = (name: string) =>
  `Hi${
    name ? ` ${name.split(' ')[0]}` : ''
  }. I'm the Distribution assistant. Ask me about orders, kitting, lots or shipping. I can also export results to Excel, build a PowerPoint deck, and email you a copy.`;

// Shown under the greeting while the conversation is empty; clicking one sends it.
export const SUGGESTIONS = [
  'How many orders are waiting for kitting, and who has them?',
  'Which lots expire in the next 90 days?',
  "Export last month's delivered orders to Excel",
  'Build a short deck of orders by status and email it to me',
  'Every Monday at 8 am, email me the orders waiting in Ready for Kitting for more than 2 days',
];

// The assistant: a conversation list beside a chat that streams answers from api/ai. The page
// only wires hooks together; the turn itself lives in useAskAiChat and utils/aiStream.
const AskAi = () => {
  const { name } = useProfile();
  const chat = useAskAiChat();
  const status = useAiStatus();
  const list = useAiConversations();
  const remove = useDeleteAiConversation();

  // Which saved conversation to load; seeded into the transcript exactly once per id, so a
  // refetch of the same conversation never resets a turn that is streaming into it.
  const [historyToLoad, setHistoryToLoad] = useState<number | null>(null);
  const seededIdRef = useRef<number | null>(null);
  const history = useAiConversation(historyToLoad);
  // Between clicking a conversation and its history arriving, a message would go to the
  // previously open one and be wiped when the history lands: hold the composer meanwhile.
  const loadingHistory =
    historyToLoad != null && historyToLoad !== chat.conversationId;
  const [listOpen, setListOpen] = useState(false);

  const { seedFromHistory } = chat;
  useEffect(() => {
    const detail = history.data;
    if (
      !detail ||
      detail.id !== historyToLoad ||
      seededIdRef.current === detail.id
    )
      return;
    seededIdRef.current = detail.id;
    seedFromHistory(detail);
  }, [history.data, historyToLoad, seedFromHistory]);

  const handleNew = () => {
    seededIdRef.current = null;
    setHistoryToLoad(null);
    chat.startNew();
    setListOpen(false);
  };

  const handleSelect = (id: number) => {
    if (id !== chat.conversationId) {
      seededIdRef.current = null;
      setHistoryToLoad(id);
    }
    setListOpen(false);
  };

  const handleDelete = (id: number) => {
    // eslint-disable-next-line no-alert
    if (!window.confirm('Delete this conversation?')) return;
    remove.mutate({ id });
    if (id === chat.conversationId) handleNew();
  };

  const unavailable =
    status.data && !status.data.available
      ? status.data.reason ||
        'The assistant is not configured on this environment.'
      : chat.unavailable;

  return (
    <AdminOnly page="AskAI">
      {unavailable ? (
        <div className="page-note chat-unavailable" role="status">
          <b>The assistant isn&apos;t available here.</b> {unavailable}
        </div>
      ) : null}
      <div className="ask-ai">
        <ConversationList
          conversations={list.data ?? []}
          selectedId={chat.conversationId}
          isLoading={list.isLoading}
          disabled={chat.isStreaming}
          open={listOpen}
          onClose={() => setListOpen(false)}
          onSelect={handleSelect}
          onDelete={handleDelete}
          onNew={handleNew}
        />
        <div className="chat">
          <div className="chat-head">
            <div>
              <b>Ask AI</b>
            </div>
            <span className="chat-sub">
              Answers come from the orders database. The assistant can read,
              never change.
            </span>
            <button
              type="button"
              className="btn-app btn-app--outline btn-app--sm chat-history-btn"
              onClick={() => setListOpen((open) => !open)}
              aria-expanded={listOpen}
            >
              History
            </button>
          </div>
          <ChatTranscript
            greeting={greeting(name)}
            messages={chat.messages}
            suggestions={
              chat.messages.length === 0 && !unavailable && !loadingHistory
                ? SUGGESTIONS
                : []
            }
            onSuggestion={chat.send}
            isThinking={chat.isThinking}
            statusText={chat.statusText}
            canRetry={chat.canRetry}
            onRetry={chat.retry}
            logRef={chat.logRef}
          />
          <ChatComposer
            disabled={Boolean(unavailable) || loadingHistory}
            isStreaming={chat.isStreaming}
            onSend={chat.send}
            onStop={chat.stop}
          />
        </div>
      </div>
    </AdminOnly>
  );
};

export default AskAi;
